import { AuditLogEntry } from '../types/user';
import { getCurrentUser } from './localDatabase';
import { encryptData, decryptData } from './encryption';

const AUDIT_LOG_KEY = 'installtrack_audit_log';
const MAX_LOG_ENTRIES = 10000; // Limit to prevent localStorage overflow

/**
 * Get all audit log entries
 */
export const getAuditLogs = (): AuditLogEntry[] => {
  try {
    const encryptedLogs = localStorage.getItem(AUDIT_LOG_KEY);
    if (!encryptedLogs) return [];
    
    const decryptedLogs = decryptData(encryptedLogs);
    return JSON.parse(decryptedLogs, (key, value) => {
      if (key === 'timestamp' && value) {
        return new Date(value);
      }
      return value;
    });
  } catch (error) {
    console.error('Error reading audit logs:', error);
    return [];
  }
};

/**
 * Save audit logs
 */
const saveAuditLogs = (logs: AuditLogEntry[]): void => {
  try {
    // Limit the number of logs to prevent localStorage overflow
    const limitedLogs = logs.slice(-MAX_LOG_ENTRIES);
    const logsJson = JSON.stringify(limitedLogs);
    const encryptedLogs = encryptData(logsJson);
    localStorage.setItem(AUDIT_LOG_KEY, encryptedLogs);
  } catch (error) {
    console.error('Error saving audit logs:', error);
  }
};

/**
 * Add a new audit log entry
 */
export const addAuditLog = (
  action: AuditLogEntry['action'],
  entityType: AuditLogEntry['entityType'],
  details: string,
  entityId?: string
): void => {
  try {
    const currentUser = getCurrentUser();
    if (!currentUser) {
      console.warn('Cannot add audit log: No user is logged in');
      return;
    }
    
    const logs = getAuditLogs();
    
    const newLog: AuditLogEntry = {
      id: crypto.randomUUID(),
      timestamp: new Date(),
      userId: currentUser.id,
      userName: currentUser.name,
      action,
      entityType,
      entityId,
      details,
      ipAddress: 'local' // In a real app, you would get the IP from the request
    };
    
    logs.push(newLog);
    saveAuditLogs(logs);
  } catch (error) {
    console.error('Error adding audit log:', error);
  }
};

/**
 * Clear all audit logs (admin only)
 */
export const clearAuditLogs = (): boolean => {
  try {
    const currentUser = getCurrentUser();
    if (!currentUser || currentUser.role !== 'admin') {
      console.warn('Only admins can clear audit logs');
      return false;
    }
    
    localStorage.removeItem(AUDIT_LOG_KEY);
    
    // Log the clearing action
    const newLog: AuditLogEntry = {
      id: crypto.randomUUID(),
      timestamp: new Date(),
      userId: currentUser.id,
      userName: currentUser.name,
      action: 'delete',
      entityType: 'system',
      details: 'Audit logs cleared',
      ipAddress: 'local'
    };
    
    saveAuditLogs([newLog]);
    return true;
  } catch (error) {
    console.error('Error clearing audit logs:', error);
    return false;
  }
};

/**
 * Export audit logs as JSON
 */
export const exportAuditLogs = (): string => {
  try {
    const logs = getAuditLogs();
    
    // Log the export action
    addAuditLog('export', 'system', 'Audit logs exported');
    
    return JSON.stringify(logs, null, 2);
  } catch (error) {
    console.error('Error exporting audit logs:', error);
    return '';
  }
};

/**
 * Filter audit logs by criteria
 */
export const filterAuditLogs = (
  filters: {
    userId?: string;
    action?: AuditLogEntry['action'];
    entityType?: AuditLogEntry['entityType'];
    startDate?: Date;
    endDate?: Date;
    searchTerm?: string;
  }
): AuditLogEntry[] => {
  try {
    let logs = getAuditLogs();
    
    if (filters.userId) {
      logs = logs.filter(log => log.userId === filters.userId);
    }
    
    if (filters.action) {
      logs = logs.filter(log => log.action === filters.action);
    }
    
    if (filters.entityType) {
      logs = logs.filter(log => log.entityType === filters.entityType);
    }
    
    if (filters.startDate) {
      logs = logs.filter(log => log.timestamp >= filters.startDate);
    }
    
    if (filters.endDate) {
      logs = logs.filter(log => log.timestamp <= filters.endDate);
    }
    
    if (filters.searchTerm) {
      const term = filters.searchTerm.toLowerCase();
      logs = logs.filter(log => 
        log.userName.toLowerCase().includes(term) ||
        log.details.toLowerCase().includes(term) ||
        (log.entityId && log.entityId.toLowerCase().includes(term))
      );
    }
    
    return logs;
  } catch (error) {
    console.error('Error filtering audit logs:', error);
    return [];
  }
};